import React, { useState, useEffect } from 'react';
import { adminService } from '../services/adminService.js';
import toast from 'react-hot-toast';

function Entities() {
  const [entities, setEntities] = useState([]);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', type: '', status: '', page: 1 });
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showStats, setShowStats] = useState(false);

  useEffect(() => {
    loadEntities();
  }, [filters.page, filters.type, filters.status]);

  const loadEntities = async () => {
    try {
      setLoading(true);
      const params = { page: filters.page, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.type) params.type = filters.type;
      if (filters.status) params.status = filters.status;

      const result = await adminService.getEntities(params);
      setEntities(result.entities || []);
      setPagination(result.pagination || { total: 0, page: 1, limit: 20, totalPages: 0 });
    } catch (err) {
      toast.error('Erro ao carregar negócios');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const result = await adminService.getEntitiesStats();
      const data = result?.response ?? result;
      const normalized = {
        totalEntities: data?.totalEntitiesCreated ?? data?.totalEntities ?? 0,
        totalEntitiesActive: data?.totalEntitiesActive ?? 0,
        avgRating: typeof data?.avgRating !== 'undefined' ? data.avgRating : null,
        ratingsCount: data?.ratingsCount ?? 0,
        entitiesWithScheduling: data?.entitiesWithScheduling ?? 0,
        entitiesByType: data?.entitiesByType ?? [],
        entitiesByState: data?.entitiesByState ?? [],
        topRatedEntities: data?.topRatedEntities ?? [],
        newEntitiesLast28Days: data?.newEntitiesLast28Days ?? data?.newEntitiesLastMonth ?? 0,
        activatedEntitiesLast28Days: data?.activatedEntitiesLast28Days ?? data?.activatedEntitiesLastMonth ?? 0,
      };
      setStats(normalized);
    } catch (err) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setStatsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  const entityTypeLabels = {
    company: 'Clínica/Hospital Veterinário',
    personal: 'Profissional Autônomo (Vet)',
    others: 'Outros',
  };

  const getEntityTypeLabel = (type) => entityTypeLabels[type] || type || '-';

  const verificationStatusLabels = {
    verified: 'Verificado',
    pending: 'Pendente',
    unverified: 'Não verificado',
    rejected: 'Rejeitado',
  };

  const getVerificationIcon = (verifiedIdentity) => {
    const title = verificationStatusLabels[verifiedIdentity] || verifiedIdentity || 'Não verificado';
    if (verifiedIdentity === 'verified') {
      return (
        <svg title={title} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
          <title>{title}</title>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <polyline points="9 12 11 14 15 10" />
        </svg>
      );
    }
    if (verifiedIdentity === 'pending') {
      return (
        <svg title={title} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
          <title>{title}</title>
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
    }
    if (verifiedIdentity === 'rejected') {
      return (
        <svg title={title} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
          <title>{title}</title>
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    }
    // unverified (default)
    return (
      <svg title={title} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
        <title>{title}</title>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters(f => ({ ...f, page: 1 }));
    loadEntities();
  };

  const handleViewDetail = async (entityId) => {
    try {
      setDetailLoading(true);
      setShowDetail(true);
      const result = await adminService.getEntityDetail(entityId);
      setSelectedEntity(result);
    } catch (err) {
      toast.error('Erro ao carregar detalhes');
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleStatus = async (entityId, currentActive) => {
    try {
      await adminService.updateEntityStatus(entityId, !currentActive);
      toast.success(`Negócio ${!currentActive ? 'ativado' : 'desativado'} com sucesso`);
      loadEntities();
      if (selectedEntity?.id === entityId) {
        setSelectedEntity(prev => ({ ...prev, active: !currentActive }));
      }
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  return (
    <div className="admin-page">
      {/* Stats section (always visible) */}

      {statsLoading ? (
        <div className="stats-grid stats-grid-3">
          {[1, 2, 3].map(i => <div key={i} className="shimmer shimmer-card"></div>)}
        </div>
      ) : (
        stats && (
          <div className="stats-grid stats-grid-5">
            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-card-info">
                  <span className="stat-label">Total de Negócios</span>
                  <span className="stat-value">{stats.totalEntities}</span>
                  <div className="stat-card-sub">
                    <span className="stat-sub-value">{stats.newEntitiesLast28Days ?? 0} nos últimos 28 dias</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-card-info">
                  <span className="stat-label">Negócios Ativos</span>
                  <span className="stat-value">{stats.totalEntitiesActive ?? 0}</span>
                  <div className="stat-card-sub">
                    <span className="stat-sub-value">{stats.activatedEntitiesLast28Days ?? 0} ativados nos últimos 28 dias</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-card-content">
                <div className="stat-card-info">
                  <span className="stat-label">Avaliação Média</span>
                  <span className="stat-value">{stats.avgRating?.toFixed(1) || '-'}</span>
                  <div className="stat-card-sub">
                    <span className="stat-sub-value">{stats.ratingsCount ?? 0} avaliações</span>
                  </div>
                </div>
              </div>
            </div>
            {/* <div className="stat-card">
            <div className="stat-card-content">
              <div className="stat-card-info">
                <span className="stat-label">Com Agendamento</span>
                <span className="stat-value">{stats.entitiesWithScheduling || 0}</span>
              </div>
            </div>
          </div> */}
          </div>
        ))}

      {/* Filters */}
      <div className="admin-filters">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Buscar por nome ou telefone..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="filter-control"
              style={{ minWidth: 280 }}
            />
          </div>
          <button type="submit" className="btn btn-primary filter-button">Buscar</button>
        </form>
        <div className="filter-group">
          <select value={filters.type} onChange={(e) => setFilters(f => ({ ...f, type: e.target.value, page: 1 }))} className="filter-control">
            <option value="">Todos os Tipos</option>
            <option value="company">Clínica ou Hospital Veterinário</option>
            <option value="personal">Profissional Autônomo</option>
            <option value="others">Outros</option>
          </select>
          <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))} className="filter-control">
            <option value="">Todos os Status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="admin-table-card">
        <div className="table-header">
          <h3>Negócios ({pagination.total})</h3>
        </div>
        {loading ? (
          <div className="table-loading">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="shimmer shimmer-row"></div>)}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Nome</th>
                  <th>Tipo</th>
                  <th>Documento</th>
                  <th>Tipo</th>
                  <th>Cidade</th>
                  <th>Avaliação</th>
                  <th>Status</th>
                  <th>Data de Criação</th>
                </tr>
              </thead>
              <tbody>
                {entities.length === 0 ? (
                  <tr><td colSpan="9" className="empty-table">Nenhum negócio encontrado</td></tr>
                ) : entities.map(entity => (
                  <tr key={entity.id}>
                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>{getVerificationIcon(entity.verifiedIdentity)}</td>
                    <td className="td-name">{entity.name}</td>
                    <td>{entity.documentType || '-'}</td>
                    <td>{entity.documentNumber || '-'}</td>
                    <td><span className="badge badge-default">{getEntityTypeLabel(entity.type)}</span></td>
                    <td>{entity.addressCity || '-'} {entity.addressUF ? `- ${entity.addressUF}` : ''}</td>
                    <td>
                      <span className="rating-display">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#F59E0B" stroke="#F59E0B" strokeWidth="1">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                        {((entity.rating ?? entity.avgRating) !== undefined && (entity.rating ?? entity.avgRating) !== null) ? ((entity.rating ?? entity.avgRating).toFixed ? (entity.rating ?? entity.avgRating).toFixed(1) : (entity.rating ?? entity.avgRating)) : '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${entity.active ? 'badge-success' : 'badge-danger'}`}>
                        {entity.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-center">{entity.createdAt ? new Date(entity.createdAt).toLocaleDateString('pt-BR') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination.totalPages > 1 && (
          <div className="table-pagination">
            <button className="btn btn-sm" disabled={filters.page === 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Anterior</button>
            <span className="pagination-info">Página {pagination.page} de {pagination.totalPages}</span>
            <button className="btn btn-sm" disabled={filters.page >= pagination.totalPages} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Próxima</button>
          </div>
        )}
      </div>

      {/* Entity Detail Modal */}
      {showDetail && (
        <div className="modal-overlay" onClick={() => setShowDetail(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalhes do Negócio</h3>
              <button className="modal-close" onClick={() => setShowDetail(false)}>&times;</button>
            </div>
            {detailLoading ? (
              <div className="modal-loading"><div className="loading-spinner"></div></div>
            ) : selectedEntity && (
              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-section">
                    <h4>Informações Gerais</h4>
                    <div className="detail-row"><span>Nome:</span><strong>{selectedEntity.name}</strong></div>
                    <div className="detail-row"><span>Tipo:</span><strong>{getEntityTypeLabel(selectedEntity.type)}</strong></div>
                    <div className="detail-row"><span>Avaliação:</span><strong>{((selectedEntity.rating ?? selectedEntity.avgRating) !== undefined && (selectedEntity.rating ?? selectedEntity.avgRating) !== null) ? ((selectedEntity.rating ?? selectedEntity.avgRating).toFixed ? (selectedEntity.rating ?? selectedEntity.avgRating).toFixed(1) : (selectedEntity.rating ?? selectedEntity.avgRating)) : '-'}</strong></div>
                    <div className="detail-row"><span>Status:</span><span className={`badge ${selectedEntity.active ? 'badge-success' : 'badge-danger'}`}>{selectedEntity.active ? 'Ativo' : 'Inativo'}</span></div>
                  </div>
                  {(selectedEntity.user || selectedEntity.User) && (
                    <div className="detail-section">
                      <h4>Proprietário</h4>
                      <div className="detail-row"><span>Nome:</span><strong>{(selectedEntity.user || selectedEntity.User).name}</strong></div>
                      <div className="detail-row"><span>Email:</span><strong>{(selectedEntity.user || selectedEntity.User).email}</strong></div>
                    </div>
                  )}
                  {(selectedEntity.Services && selectedEntity.Services.length > 0) || (selectedEntity.services && selectedEntity.services.length > 0) ? (
                    <div className="detail-section">
                      <h4>Serviços ({(selectedEntity.Services || selectedEntity.services || []).length})</h4>
                      <div className="tags-list">
                        {(selectedEntity.Services || selectedEntity.services || []).map((s, i) => (
                          <span key={i} className="tag">{s.name}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {(selectedEntity.Plans && selectedEntity.Plans.length > 0) || selectedEntity.plan ? (
                    <div className="detail-section">
                      <h4>Planos</h4>
                      {(selectedEntity.Plans && selectedEntity.Plans.length > 0) ? (
                        selectedEntity.Plans.map((p, i) => (
                          <div key={i} className="detail-card-mini">
                            <strong>{p.name || p.planId}</strong>
                            <span className={`badge ${p.active ? 'badge-success' : 'badge-danger'}`}>{p.active ? 'Ativo' : 'Inativo'}</span>
                          </div>
                        ))
                      ) : (
                        <div className="detail-card-mini">
                          <strong>{selectedEntity.plan.plan || selectedEntity.plan.planId}</strong>
                          <span className={`badge ${selectedEntity.plan.active ? 'badge-success' : 'badge-danger'}`}>{selectedEntity.plan.active ? 'Ativo' : 'Inativo'}</span>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
                <div className="modal-actions">
                  <button className={`btn ${selectedEntity.active ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggleStatus(selectedEntity.id, selectedEntity.active)}>
                    {selectedEntity.active ? 'Desativar Negócio' : 'Ativar Negócio'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Entities;
